import { Dialog as DialogPrimitive } from 'radix-ui';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useGames } from '@/data/games';
import { useSearch } from '@/data/vault';
import { pathFor } from '@/vault/model';
import { groupHits, isQuickSearchKey } from './model';
import { SearchResults } from './SearchResults';

/** Ctrl+K (Cmd+K on a Mac) from anywhere: quick search across Hive. Enter opens the highlighted result. */
export function QuickSearch() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!isQuickSearchKey(e)) return;
      e.preventDefault();
      setOpen((o) => !o);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      {open ? <Palette onClose={() => setOpen(false)} /> : null}
    </DialogPrimitive.Root>
  );
}

function Palette({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const results = useSearch(query, { limit: 20 });
  const games = useGames();
  const navigate = useNavigate();
  // In the order they show: grouped by type.
  const ordered = groupHits(results.data ?? []).flatMap((g) => g.hits);
  const active = ordered[Math.min(cursor, ordered.length - 1)];

  function openActive() {
    if (active) {
      void navigate(
        pathFor(
          { type: active.item_type, id: active.item_id, game_id: active.game_id, parent_id: active.parent_id },
          games.data ?? [],
        ),
      );
    } else if (query.trim()) {
      void navigate(`/scan?q=${encodeURIComponent(query.trim())}`);
    }
    onClose();
  }

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60" />
      <DialogPrimitive.Content
        aria-describedby={undefined}
        className="fixed top-[12vh] left-1/2 z-50 flex max-h-[70vh] w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 flex-col border border-magenta bg-bg-2 shadow-[0_0_32px_rgb(255_43_214/0.2)] outline-none"
      >
        <DialogPrimitive.Title className="sr-only">Quick search</DialogPrimitive.Title>
        <div className="flex items-center gap-2 border-b border-line px-3">
          <Search className="size-4 text-muted" aria-hidden />
          <input
            autoFocus
            aria-label="Quick search"
            placeholder="Search Hive…"
            className="flex-1 bg-transparent py-3 text-base text-text outline-none placeholder:text-dim"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, ordered.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                openActive();
              }
            }}
          />
          <kbd className="font-mono text-[10px] text-dim">Esc</kbd>
        </div>
        <div className="overflow-y-auto p-3">
          {query.trim() && results.isSuccess && ordered.length === 0 ? (
            <p className="text-sm text-dim">Nothing matches.</p>
          ) : null}
          {ordered.length ? (
            <SearchResults
              hits={ordered}
              active={active ? `${active.item_type}:${active.item_id}` : null}
              onOpen={onClose}
            />
          ) : null}
          {!query.trim() ? (
            <p className="text-sm text-dim">Tasks, ideas, docs, links and files. Enter opens the highlighted one.</p>
          ) : null}
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
